import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { adminKYCApi } from '@/api/admin/kyc';
import { QuestionnaireView } from '@/modules/Questionnaire/View';
import { scrollToTop } from '@/routing';
import { SHOWED_SECTIONS_COUNT } from '@/utils/static/kyc';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { kycHelpers } from '@/utils/helpers/kyc';
import { usePopups, useLoadingState } from '@/hooks';
import { downloadImgFromBlob } from '@/utils/helpers';

export const SumsubAnswers = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { showErrorToast, showInfoToast } = usePopups();
  const [currentSection, setCurrentSection] = useState(0);
  const [preFilledQuestions, setPreFilledQuestions] = useState({});
  const [creators, setCreators] = useState({});
  const [defaultValues, setDefaultValues] = useState(null);
  const { data: res, isLoading } = useQuery({
    queryKey: ['getInvestorSumSubAnswers', id],
    queryFn: () => adminKYCApi.getInvestorSumSubAnswers(id)
  });
  const { data: sumSubStatusRes, refetch: refetchStatus } = useQuery({
    queryKey: ['getInvestorSumSubStatus', id],
    queryFn: () => adminKYCApi.getInvestorSumSubStatus(id),
    refetchOnWindowFocus: true,
    refetchInterval: 30000
  });
  const { mutateAsync: setSumSubStatus } = useMutation({
    mutationFn: adminKYCApi.setInvestorSumSubStatus
  });

  const questionnaire = useMemo(() => (res ? res.data : null), [res]);

  const questionnaireTypeGroup = questionnaire?.[0]?.visibleTypeGroup;

  const { data: verificationPhotos } = useQuery({
    queryKey: ['getVerificationPhotos'],
    queryFn: () =>
      adminKYCApi.getVerificationPhotos({
        questionSectionId: questionnaire?.[0].id,
        investorId: id
      }),
    retry: false,
    enabled: !!questionnaireTypeGroup
  });
  const { mutateAsync: getVerificationPhoto } = useMutation({
    mutationFn: adminKYCApi.getVerificationPhoto
  });

  const {
    control,
    handleSubmit,
    trigger,
    watch,
    formState: { errors, submitCount },
    reset
  } = useForm({ defaultValues: { ...defaultValues } });

  const handleVerificationPhoto = async (mediaId, imageTitle) => {
    try {
      const response = await getVerificationPhoto({
        questionSectionId: questionnaire?.[0].id,
        mediaId,
        investorId: id
      });

      if (response) {
        downloadImgFromBlob(response.data.data, `${imageTitle}.jpg`, 'image/jpeg', true);
      }
    } catch (e) {
      console.error(e);
      showErrorToast(e, true);
    }
  };

  useLoadingState(isLoading);

  useEffect(() => {
    try {
      if (questionnaire) {
        const { values, lastAnsweredSection, preFilledQuestions, creators } =
          kycHelpers.collectQuestionnaireAnswers({ questionSections: questionnaire });

        if (Object.keys(preFilledQuestions).length > 0) {
          setPreFilledQuestions(preFilledQuestions);
        }
        if (Object.keys(creators).length > 0) {
          setCreators(creators);
        }

        setDefaultValues(values);
        reset(values);
      }
    } catch (e) {
      console.error(e);
      showErrorToast(e, true);
    }
  }, [questionnaire]);

  const stepsCount = useMemo(() => {
    if (questionnaire && !!questionnaire[0].questionGroups.length) {
      return Math.ceil(questionnaire[0].questionGroups.length / SHOWED_SECTIONS_COUNT);
    }
    return 0;
  }, [questionnaire]);

  const statusSettings = useMemo(() => {
    const status = sumSubStatusRes?.data?.investorVerificationQuestionSection?.status;

    if (status) {
      const isRejected = status === 'Rejected';
      const isApproved = status === 'Accepted';
      return {
        isRejected: isRejected,
        isApproved: isApproved,
        allowReject: !isRejected && status !== 'New',
        allowApprove: !isApproved
      };
    }

    return null;
  }, [sumSubStatusRes, questionnaire]);

  const onNext = () => {
    if (currentSection + 1 < stepsCount) {
      setCurrentSection(currentSection + 1);
      scrollToTop();
    }
  };
  const onBack = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
      scrollToTop();
    }
  };

  const setStatus = async (status, successText) => {
    try {
      await setSumSubStatus({
        investorId: id,
        status: status,
        questionSectionId: questionnaire?.[0].id
      });
      showInfoToast(successText);
      await refetchStatus();
    } catch (e) {
      showErrorToast(e, true);
      console.error(e);
    }
  };

  return (
    <div>
      {!isLoading && res && res.data && (
        <QuestionnaireView
          questionnaire={{ questionSections: res.data }}
          filteredQuestionnaire={res.data}
          currentSection={currentSection}
          onNext={onNext}
          onBack={onBack}
          acceptQuestionnaire={() => setStatus('Accepted', t('Accepted'))}
          rejectQuestionnaire={() => setStatus('New', t('Rejected'))}
          statusSettings={statusSettings}
          adminView
          control={control}
          handleSubmit={handleSubmit}
          trigger={trigger}
          watch={watch}
          errors={errors}
          submitCount={submitCount}
          preFilledQuestions={preFilledQuestions}
          creators={creators}
          verificationPhotos={verificationPhotos}
          handleVerificationPhoto={handleVerificationPhoto}
          isVeriffQuestionnaire={true}
          sumSubStatusRes={sumSubStatusRes}
          refetchSumSubRes={refetchStatus}
        />
      )}

      {(!res || !res.data) && <div>Something went wrong</div>}
    </div>
  );
};
